import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ApiManager from "../../api/ApiManager.tsx";
import { BillingAddress, ShippingAddress } from "../user/userSlice.types.ts";
import { PaymentMethodType } from "./checkoutSlice.types.ts";
import { fetchInit } from "../user/userSlice.ts";
import LocalStorage, { DELIVERY_ADDRESS } from "../../helpers/localStorage.ts";
import { fetchDeliveryMethods } from "./cartSlice.ts";
import { placeOrder } from "./cartSlice.ts";

export const COD_PAYMENT_ID = 1;

export const fetchPaymentMethods =
    createAsyncThunk('cart/fetchPaymentMethods', () => ApiManager.fetchPaymentMethods());
export const validateBillingAddress =
    createAsyncThunk('cart/validateBillingAddress', (data: BillingAddress) => ApiManager.validateBillingAddress(data));
export const validateShippingAddress =
    createAsyncThunk('cart/validateShippingAddress', (data: ShippingAddress) => ApiManager.validateShippingAddress(data));
export const autoCompleteBillingAddress =
    createAsyncThunk('cart/autoCompleteBillingAddress', (nip: string) => ApiManager.autoCompleteBillingAddress(nip))

type CheckoutSliceStateType = {
    billingAddress: {
        data: null | BillingAddress;
        valid: boolean | null;
        loaded: boolean;
        loading: boolean;
        error: string | null;
    };
    shippingAddress: {
        data: null | ShippingAddress;
        valid: boolean | null;
        loaded: boolean;
        loading: boolean;
        error: string | null;
    };
    paymentMethod: number | null;
    availablePaymentMethods: {
        methods: PaymentMethodType[];
        codAvailable: boolean;
        loaded: boolean;
        error: string | null;
    };
    rules: {
        [checkKey: string]: boolean,
    };
    isSubmitting: boolean;
    orderStatus: null | number;
    orderInvoice: null | string;
}

const checkoutSlice = createSlice({
    name: 'checkout',
    initialState: {
        billingAddress: {
            data: null,
            valid: null,
            loaded: false,
            loading: false,
            error: null,
        },
        shippingAddress: {
            data: (new LocalStorage).get(DELIVERY_ADDRESS) as ShippingAddress,
            valid: null,
            loaded: false,
            loading: false,
            error: null,
        },
        paymentMethod: null,
        availablePaymentMethods: {
            methods: [],
            loaded: false,
            loading: false,
            codAvailable: true,
            error: null,
        },
        rules: {
            checkStatute: false,
            checkPolicy: false,
            checkMail: false,
            checkPhone: false,
        },
        isSubmitting: false,
        orderStatus: null,
        orderInvoice: null,
    } as CheckoutSliceStateType,
    reducers: {
        setBillingAddress(state, action) {
            state.billingAddress = { ...state.billingAddress, data: action.payload, valid: true };
        },
        setBillingAddressValid(state, action) {
            state.billingAddress.valid = action.payload;

            if (action.payload && !state.shippingAddress.loaded) {
                state.shippingAddress.data = state.billingAddress.data;
                state.shippingAddress.loaded = true;
            }
        },
        setShippingAddress(state, action) {
            state.shippingAddress = { ...state.shippingAddress, data: action.payload, valid: true };
        },
        setShippingAddressValid(state, action) {
            state.shippingAddress.valid = action.payload;
        },
        setPaymentMethod(state, action) {
            state.paymentMethod = action.payload;
        },
        setRules(state, action) {
            const { id, value } = action.payload;
            if (!state.rules) state.rules = {};
            state.rules[id] = value;
        },
        setIsSubmitting(state, action) {
            state.isSubmitting = action.payload;
        },
        setOrderStatus(state, action) {
            state.orderStatus = action.payload;
        },
        setOrderInvoice(state, action) {
            state.orderInvoice = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(validateBillingAddress.pending, (state) => {
                state.billingAddress.loading = true;
            })
            .addCase(validateBillingAddress.fulfilled, (state) => {
                state.billingAddress.loading = false;
            })
            .addCase(validateBillingAddress.rejected, (state) => {
                state.billingAddress.loading = false;
            })
            .addCase(validateShippingAddress.pending, (state) => {
                state.shippingAddress.loading = true;
            })
            .addCase(fetchDeliveryMethods.fulfilled, (state) => {
                state.shippingAddress.loading = false;
            })
            .addCase(fetchDeliveryMethods.rejected, (state) => {
                state.shippingAddress.loading = false;
            })
            //.addCase(validateBillingAddress.)
            .addCase(fetchInit.pending, (state) => {
                state.billingAddress.loading = true;
                state.billingAddress.loaded = false;
                state.billingAddress.error = null;
            })
            .addCase(fetchInit.fulfilled, (state, action) => {
                const user = action.payload.user;
                const billingAddress = {
                    country: action.payload.country.toUpperCase(),
                    company: user?.company,
                    nip: user?.nip,
                    firstName: user?.firstName,
                    lastName: user?.lastName,
                    street: user?.address.street,
                    house: user?.address.firstNumber,
                    apartment: user?.address.secondaryNumber,
                    postalCode: user?.address.postCode,
                    city: user?.address.city,
                    email: user?.email,
                    phone: user?.phone,
                } as BillingAddress;

                state.billingAddress.loading = false;
                state.billingAddress.loaded = true;
                state.billingAddress.data = billingAddress
                //state.billingAddress.valid = billingValidator(billingAddress);
                //console.log(billingValidator(billingAddress));
            })
            .addCase(fetchInit.rejected, (state, action) => {
                state.billingAddress.loading = false;
                state.billingAddress.loaded = true;
                state.billingAddress.error = action.error as string | null;
            })
            .addCase(fetchPaymentMethods.pending, (state) => {
                state.availablePaymentMethods.loaded = false;
                state.availablePaymentMethods.error = null;
                state.paymentMethod = null;
            })
            .addCase(fetchPaymentMethods.fulfilled, (state, action) => {
                state.availablePaymentMethods.methods = action.payload as [];
                state.availablePaymentMethods.loaded = true;

                if (action.payload.length) {
                    state.paymentMethod = action.payload[0].id;
                }
            })
            .addCase(fetchPaymentMethods.rejected, (state, action) => {
                state.availablePaymentMethods.error = action.error.message as string | null;
                state.availablePaymentMethods.loaded = true;
            })
            .addCase(placeOrder.rejected, (state) => {
                state.isSubmitting = false
            });
    },
});

export const {
    setBillingAddress,
    setBillingAddressValid,
    setShippingAddress,
    setPaymentMethod,
    setRules,
    setIsSubmitting,
    setOrderStatus,
    setOrderInvoice
} = checkoutSlice.actions;
export default checkoutSlice.reducer;